import { Controller, useFormContext, UseFormSetValue } from "react-hook-form";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { Membership, MembershipGroup } from "@/types/system-setting/membership";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import React, { useEffect, useMemo, useState } from "react";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  useGetCategoriesQuery,
} from "@/services/categoryApi";
import { DayOfWeek, LimitedAccessTime } from "@/types";
import { daysOrder } from "@/lib/helper";
import { initialDaysOfWeek } from "@/lib/constants/membership";
import { status } from "@/lib/constants/global";
import { Card } from "@/components/ui/card";
import Combobox from "@/components/ui/common/combobox";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormProvider, useForm } from "react-hook-form";
import { useToast } from "@/components/ui/use-toast";

const BasicInfoForm = () => {
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
    register,
    watch,
  } = useFormContext<Membership>();
  const { toast } = useToast();
  const watcher=watch();

  const [access, setAccess] = useState<string | undefined>("");
  // const [isGroupModalOpen, setIsGroupModalOpen] = useState<boolean>(false);

  const [limitedAccessDays, setLimitedAccessDays] = useState<LimitedAccessTime>(
    Object.keys(getValues("limited_access_time") as LimitedAccessTime).length >
      0
      ? (getValues("limited_access_time") as LimitedAccessTime)
      : initialDaysOfWeek
  );

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const { data: groupData, isFetching, refetch } = useGetCategoriesQuery({
    org_id: orgId,
    query: "type=credit_add_ons",
  });

  const groupOptions = useMemo(() => {
    return Array.isArray(groupData?.data)
      ? groupData.data.map((cat) => ({
        label: cat.name,
        value: String(cat.id)
      }))
      : [];
  }, [groupData]);
  const handleAdd = (day: string) => {
    const dayIntervals = limitedAccessDays[day as string] || [];

    if (dayIntervals.length > 4) {
      toast({
        variant: "destructive",
        title: "Limit Reached",
        description: `You cannot add more than 5 slots for ${day}.`,
      });
      return;
    }

    setLimitedAccessDays((prev) => ({
      ...prev,
      [day]: [...dayIntervals, { from_time: "", to_time: "" }],
    }));
  };

  const handleDelete = (day: string, index: number) => {
    setLimitedAccessDays((prev) => {
      const dayIntervals = prev[day] || [];

      // If there's only one interval, clear its fields instead of deleting
      if (dayIntervals.length === 1) {
        return {
          ...prev,
          [day]: [{ from_time: "", to_time: "" }],
        };
      }

      // Otherwise, delete the interval at the specified index
      return {
        ...prev,
        [day]: dayIntervals.filter((_, i) => i !== index),
      };
    });
  };

  const handleTimeChange = (
    day: string,
    index: number,
    field: "from_time" | "to_time",
    value: string
  ) => {
    setLimitedAccessDays((prev) => ({
      ...prev,
      [day]: prev[day].map((interval, i) =>
        i === index ? { ...interval, [field]: value } : interval
      ),
    }));
  };

  const sortedDays = Object.keys(limitedAccessDays).sort(
    (a, b) =>
      daysOrder.indexOf(a as DayOfWeek) - daysOrder.indexOf(b as DayOfWeek)
  );



  useEffect(() => {
    if (limitedAccessDays) {
      setValue("limited_access_time", limitedAccessDays);
    }
  }, [limitedAccessDays]);


  return (
    <div className="text-black space-y-4">
      {/* <GroupForm
        isOpen={isGroupModalOpen}
        setOpen={setIsGroupModalOpen}
        refetch={refetch}
        setValue={setValue}
      /> */}
      <Card className="p-4">
        <h1 className="font-semibold text-[#2D374] text-xl">Basic Information</h1>
        <div className="grid grid-cols-3 gap-4 items-start my-3">
          <div className="flex flex-col gap-4 col-span-1">
            <FloatingLabelInput
              id="membership_name"
              label="Name"
              text="*"
              {...register("name", {
                required: "Required",
                setValueAs: (value) => value.toLowerCase(),
                maxLength: {
                  value: 40,
                  message: "Name must be 40 characters or less",
                },
              })}
              error={errors.name?.message}
              className="capitalize"
            />
            <Controller
              name="group_id"
              // defaultValue={}
              rules={{ required: "Required" }}
              control={control}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { invalid, error },
              }) => {
                // Properly handle null, undefined, or empty values
                // group_id is a number, so we only check for null/undefined
                const hasValidValue = value !== null && value !== undefined;
                const defaultValue = hasValidValue
                  ? groupOptions.find(option => option.value === String(value))?.label || ""
                  : "";

                return (
                  <div className="relative flex gap-2 items-center">
                    <div className="flex-1">
                      <Combobox
                        list={groupOptions}
                        defaultValue={defaultValue}
                        setFilter={(selectedLabel: string) => {
                          const selectedOption = groupOptions.find(option => option.label === selectedLabel);
                          onChange(selectedOption ? Number(selectedOption.value) : undefined);
                        }}
                        disabled={isFetching}
                        label="Group"
                        showLabel={false}
                      />
                      {invalid && (
                        <span className="text-destructive block !mt-[5px] text-[12px]">
                          {error?.message}
                        </span>
                      )}
                    </div>
                    {/* <Button
                      onClick={() => setIsGroupModalOpen(true)}
                      title="Add New Group"
                      className="p-2 shrink-0 bg-primary border-none hover:bg-primary/70"
                      variant="outline"
                    >
                      <Plus className="h-4 w-4 text-white" />
                    </Button> */}
                  </div>
                );
              }}
            />
          </div>
          <div >
            <FloatingLabelInput
              id="description"
              label="Description"
              type="textarea"
              rows={4}
              className="custom-scrollbar col-span-2 peer-placeholder-shown:top-[10%]"
              {...register("description", {
                maxLength: {
                  value: 350,
                  message: "Description should not exceed 350 characters",
                },
              })}
              error={errors.description?.message}
            />
          </div>
          <Controller
            name="status"
            rules={{ required: "Required" }}
            control={control}
            render={({
              field: { onChange, value, onBlur },
              fieldState: { invalid, error },
            }) => {
              const statusLabel = status.filter((r) => r.value == value)[0];
              return (
                <Select
                  onValueChange={(value) => onChange(value)}
                  defaultValue={value}
                >
                  <SelectTrigger floatingLabel="Status" text="*">
                    <SelectValue
                      placeholder="Select status"
                      className="text-gray-400"
                    >
                      <span className="flex gap-2 items-center">
                        <span
                          className={`${statusLabel?.color} rounded-[50%] w-4 h-4`}
                        ></span>
                        <span>{statusLabel?.label}</span>
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {status.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            }}
          />
        </div>

        <h1 className="font-semibold text-[#2D374] text-xl">Credit Scope</h1>
        <div className="flex items-center gap-6  ">
          <div className="flex items-center gap-4">
            <Label className="font-semibold">
              Duration<span className="text-red-500">*</span>
            </Label>
            <Controller
              name="duration_period"
              rules={{ required: "Required" }}
              control={control}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { invalid, error },
              }) => (
                <div>
                  <Select
                    onValueChange={(value) => {
                      onChange(value);
                    }}
                    value={value}
                  >
                    <SelectTrigger name="duration_period">
                      <SelectValue
                        placeholder="Select duration"
                        defaultValue={undefined}
                      />
                    </SelectTrigger>
                    {invalid && (
                      <span className="text-destructive block !mt-[5px] text-[12px]">
                        {error?.message}
                      </span>
                    )}
                    <SelectContent>
                      <SelectItem value={"month"}>Month</SelectItem>
                      <SelectItem value={"quarter"}>Quarter</SelectItem>
                      <SelectItem value={"year"}>Year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            />
            <FloatingLabelInput
              id="duration"
              type="number"
              className="w-20 "
              {...register("duration", {
                required: "Required",
                setValueAs: (value) => {
                  if (value === "" || value === null || value === undefined) {
                    return undefined;
                  }
                  return Number(value);
                },
                min: {
                  value: 1,
                  message: "Duration must be between 1 and 12.",
                },
                max: {
                  value: 12,
                  message: "Duration must be between 1 and 12.",
                },
              })}
              error={errors.duration?.message}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

// interface GroupFormProps {
//   isOpen: boolean;
//   setOpen: React.Dispatch<React.SetStateAction<boolean>>;
//   refetch: () => void;
//   setValue: UseFormSetValue<Membership>;
// }

// const GroupForm = ({ isOpen, setOpen, refetch, setValue }: GroupFormProps) => {
//   const [createCategory] = useCreateCategoryMutation();
//   const { toast } = useToast();

//   const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

//   const form = useForm<MembershipGroup>({
//     mode: "all",
//     defaultValues: {
//       name: "",
//     },
//   });

//   const {
//     control,
//     register,
//     handleSubmit,
//     clearErrors,
//     reset,
//     formState: { errors, isSubmitting },
//   } = form;

//   const handleClose = () => {
//     reset({ name: "" });
//     clearErrors();
//     setOpen(false);
//   };

//   const onSubmit = async (data: MembershipGroup) => {
//     try {
//       const payload = {
//         org_id: orgId,
//         name: data.name.toLowerCase(),
//         type: "credit_add_ons" as const,
//       };
//       const resp = await createCategory(payload).unwrap();
//       if (resp) {
//         await refetch();
//         toast({
//           title: "Success",
//           variant: "success",
//           description: "Group created successfully",
//         });
//         setValue("group_id", resp.id as number);
//         handleClose();
//       }
//     } catch (error: unknown) {
//       console.error("Error creating group", { error });
//       if (error && typeof error === "object" && "data" in error) {
//         const typedError = error as { data?: { detail?: string; message?: string } };
//         toast({
//           variant: "destructive",
//           title: "Error creating group",
//           description: typedError.data?.detail ?? typedError.data?.message ?? "Something went wrong",
//         });
//       } else {
//         toast({
//           variant: "destructive",
//           title: "Error creating group",
//           description: "Something went wrong",
//         });
//       }
//     }
//   };

//   return (
//     <Dialog open={isOpen} onOpenChange={handleClose}>
//       <DialogContent className="sm:max-w-[425px]">
//         <DialogHeader>
//           <DialogTitle>Create Group</DialogTitle>
//         </DialogHeader>
//         <FormProvider {...form}>
//           <form
//             noValidate
//             onSubmit={(e) => {
//               e.preventDefault();
//               e.stopPropagation();
//               handleSubmit(onSubmit)(e);
//             }}
//           >
//             <div className="space-y-4">
//               <div className="relative">
//                 <FloatingLabelInput
//                   id="name"
//                   placeholder="Enter group name"
//                   {...register("name", {
//                     required: "Required",
//                     validate: (value) => {
//                       if (value.trim() === "") {
//                         return "Name should not be empty";
//                       }
//                       return true;
//                     },
//                     maxLength: {
//                       value: 50,
//                       message: "Name should be less than 50 characters",
//                     },
//                   })}
//                   error={errors.name?.message}
//                 />
//               </div>

//               <div className="flex gap-2 justify-end">
//                 <Button
//                   type="button"
//                   variant="outline"
//                   onClick={handleClose}
//                   disabled={isSubmitting}
//                 >
//                   Cancel
//                 </Button>
//                 <Button
//                   type="submit"
//                   disabled={isSubmitting}
//                   className="flex gap-2"
//                 >
//                   {isSubmitting && <i className="fa fa-circle-notch animate-spin"></i>}
//                   {!isSubmitting && <i className="fa fa-floppy-disk"></i>}
//                   Save
//                 </Button>
//               </div>
//             </div>
//           </form>
//         </FormProvider>
//       </DialogContent>
//     </Dialog>
//   );
// };

export default BasicInfoForm;
